import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  GraduationCap,
  Loader2,
  AlertCircle,
  ArrowLeft,
  KeyRound,
  CheckCircle2,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  // checking | valid | invalid | done
  const [status, setStatus] = useState("checking");
  const [emailMasked, setEmailMasked] = useState("");
  const [form, setForm] = useState({ new_password: "", confirm_password: "" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/auth/reset-password/${encodeURIComponent(token)}`);
        if (data.valid) {
          setEmailMasked(data.email_masked || "");
          setStatus("valid");
        } else {
          setStatus("invalid");
        }
      } catch {
        setStatus("invalid");
      }
    })();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.new_password.length < 8) {
      setError("La nuova password deve avere almeno 8 caratteri.");
      return;
    }
    if (form.new_password !== form.confirm_password) {
      setError("Le password non coincidono.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/reset-password", { token, ...form });
      setStatus("done");
      setTimeout(() => navigate("/login", { replace: true }), 3000);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="reset-password-page"
      className="min-h-screen bg-brand-bg brand-grid-bg flex flex-col"
    >
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <Link
          to="/login"
          data-testid="reset-back-login"
          className="inline-flex items-center gap-1.5 text-sm text-brand-blue hover:underline underline-offset-4"
        >
          <ArrowLeft size={16} /> Torna all’accesso
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 pb-16">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2.5 justify-center mb-8">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-sm bg-brand-blue text-white">
              <GraduationCap size={22} strokeWidth={2} />
            </span>
            <div className="leading-tight">
              <div className="wordmark text-brand-blue text-xl">Buro Academy</div>
              <div className="text-xs text-brand-muted -mt-0.5">Burocratica Servizi</div>
            </div>
          </div>

          <div className="bg-white border border-brand-line rounded-sm shadow-sm p-8">
            <div className="gold-rule" />

            {status === "checking" && (
              <div
                data-testid="reset-checking"
                className="flex items-center gap-2 text-sm text-brand-muted py-4"
              >
                <Loader2 size={18} className="animate-spin" /> Verifica del link in corso…
              </div>
            )}

            {status === "invalid" && (
              <div data-testid="reset-invalid">
                <h1 className="text-2xl text-brand-ink mb-2">Link non valido</h1>
                <p className="text-sm text-brand-muted mb-6">
                  Il link di reimpostazione è scaduto, è già stato utilizzato oppure
                  non è corretto. Richiedine uno nuovo per continuare.
                </p>
                <Link
                  to="/password-dimenticata"
                  data-testid="reset-request-new"
                  className="btn-primary w-full inline-flex items-center justify-center gap-2"
                >
                  Richiedi un nuovo link
                </Link>
              </div>
            )}

            {status === "done" && (
              <div data-testid="reset-done">
                <div className="flex items-center gap-2 text-brand-blue mb-3">
                  <CheckCircle2 size={22} />
                  <h1 className="text-2xl text-brand-ink">Password aggiornata</h1>
                </div>
                <p className="text-sm text-brand-muted mb-6">
                  La nuova password è attiva. Verrai reindirizzato alla pagina di
                  accesso tra qualche istante.
                </p>
                <Link
                  to="/login"
                  data-testid="reset-goto-login"
                  className="btn-primary w-full inline-flex items-center justify-center gap-2"
                >
                  Vai all’accesso
                </Link>
              </div>
            )}

            {status === "valid" && (
              <>
                <h1 className="text-2xl text-brand-ink mb-1">Scegli una nuova password</h1>
                <p className="text-sm text-brand-muted mb-6">
                  {emailMasked ? (
                    <>
                      Account: <span className="font-semibold text-brand-ink">{emailMasked}</span>.{" "}
                    </>
                  ) : null}
                  La password deve avere almeno 8 caratteri e deve essere diversa
                  dalla precedente.
                </p>

                <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-form">
                  <div>
                    <Label htmlFor="new_password" className="text-sm text-brand-ink">
                      Nuova password
                    </Label>
                    <Input
                      id="new_password"
                      data-testid="reset-new-password"
                      type="password"
                      autoComplete="new-password"
                      minLength={8}
                      value={form.new_password}
                      onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                      placeholder="••••••••"
                      className="mt-1.5 h-11 rounded-sm border-brand-line focus-visible:ring-2 focus-visible:ring-brand-blue"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="confirm_password" className="text-sm text-brand-ink">
                      Conferma nuova password
                    </Label>
                    <Input
                      id="confirm_password"
                      data-testid="reset-confirm-password"
                      type="password"
                      autoComplete="new-password"
                      minLength={8}
                      value={form.confirm_password}
                      onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                      placeholder="••••••••"
                      className="mt-1.5 h-11 rounded-sm border-brand-line focus-visible:ring-2 focus-visible:ring-brand-blue"
                      required
                    />
                  </div>

                  {error && (
                    <div
                      data-testid="reset-error"
                      role="alert"
                      className="flex items-start gap-2 rounded-sm border border-red-200 bg-red-50 p-3 text-sm text-red-800"
                    >
                      <AlertCircle size={16} className="mt-0.5 shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    data-testid="reset-submit-btn"
                    disabled={saving}
                    className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <Loader2 size={18} className="animate-spin" /> Salvataggio…
                      </>
                    ) : (
                      <>
                        <KeyRound size={16} /> Imposta la nuova password
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
